import { useMemo } from "react";
import { isTelegramWebApp } from "../utils/telegram";

/**
 * Поверхность, на которой запущено приложение.
 *
 *  - `telegram` — мы внутри Telegram Mini App (есть `window.Telegram.WebApp`
 *                 или включён dev-mock).
 *  - `web`      — обычный браузер: сайт, открытый по URL.
 */
export type AppSurface = "telegram" | "web";

export interface AppPlatformInfo {
  /** Сокращение: открыто внутри Telegram Mini App. */
  isTelegram: boolean;
  /** Сокращение: открыто как обычный сайт. */
  isWeb: boolean;
  /** Дискриминированное значение — удобно для switch / условного рендера. */
  surface: AppSurface;
  /**
   * Платформа Telegram-клиента (`ios` | `android` | `tdesktop` | `macos` | `weba` | …)
   * или `null` для обычного веба. Полезно, когда верстка/инструкции отличаются по ОС
   * (например, deep-link на ios vs intent на android).
   */
  telegramPlatform: string | null;
}

/**
 * Кешируем на уровне модуля: поверхность приложения не меняется в течение жизни
 * вкладки, поэтому нет смысла пересчитывать на каждый рендер/маунт.
 */
let cached: AppPlatformInfo | null = null;

function detect(): AppPlatformInfo {
  const isTelegram = isTelegramWebApp();

  let telegramPlatform: string | null = null;
  if (isTelegram && typeof window !== "undefined") {
    // Читаем напрямую (а не через getTelegramWebApp), чтобы не вызывать `tg.ready()`
    // как побочный эффект простого чтения метаданных.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tg = (window as any).Telegram?.WebApp;
    telegramPlatform = typeof tg?.platform === "string" ? tg.platform : null;
  }

  return {
    isTelegram,
    isWeb: !isTelegram,
    surface: isTelegram ? "telegram" : "web",
    telegramPlatform
  };
}

/**
 * Единый источник правды о том, откуда открыто приложение.
 *
 * Используйте, когда нужно поменять разметку/поведение в зависимости от
 * поверхности. Возвращаемое значение стабильно между рендерами (`useMemo`
 * + module-level cache), поэтому безопасно класть в массивы зависимостей.
 *
 * @example
 *   const { isTelegram } = usePlatform();
 *   return isTelegram ? <TelegramHeader /> : <WebHeader />;
 */
export function usePlatform(): AppPlatformInfo {
  return useMemo(() => {
    if (!cached) cached = detect();
    return cached;
  }, []);
}
